import { supabase } from '../supabaseClient';
import { dayKey, dayRange } from './practiceTime';

/**
 * Appointment lifecycle.
 *
 * Every status change goes through change_appointment_status, which updates the
 * appointment, appends to the event log and maintains the cancellation counter
 * in one transaction. Never update `status` directly — a status written without
 * its event silently corrupts every duration metric.
 */

export const STATUS = {
  // Asked for in hours that need the professional's approval (0055).
  REQUESTED: 'requested',
  SCHEDULED: 'scheduled',
  WAITING: 'waiting',
  IN_SESSION: 'in_session',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
};

export const STATUS_TONE = {
  requested: 'warning',
  scheduled: 'info',
  waiting: 'warning',
  in_session: 'indigo',
  completed: 'success',
  cancelled: 'neutral',
  no_show: 'danger',
};

/** What a professional may move an appointment to from where it is now. */
export const NEXT_STATUSES = {
  // Answered in Solicitudes, not moved through the agenda.
  requested: [],
  scheduled: ['waiting', 'in_session', 'cancelled', 'no_show'],
  waiting: ['in_session', 'cancelled', 'no_show'],
  in_session: ['completed'],
  completed: [],
  cancelled: [],
  no_show: [],
};

/**
 * Who asked to cancel and when the patient gave notice decide whether a
 * cancellation is charged (migration 0052). Sent only when given, so a call
 * without them reaches the function the same way it always did.
 */
export const changeStatus = ({
  appointmentId, toStatus, source = 'professional', billable = null, reason = null,
  cancelledBy = null, noticeAt = null,
}) =>
  supabase.rpc('change_appointment_status', {
    p_appointment_id: appointmentId,
    p_to_status: toStatus,
    p_source: source,
    p_billable: billable,
    p_cancellation_reason: reason,
    ...(cancelledBy ? { p_cancelled_by: cancelledBy } : {}),
    ...(noticeAt ? { p_notice_at: noticeAt } : {}),
  });

/** Cancellation available to patients, who hold no direct write access. */
export const cancelAppointment = (appointmentId, reason = null) =>
  supabase.rpc('cancel_my_appointment', {
    p_appointment_id: appointmentId,
    p_reason: reason,
  });

/**
 * Charging a cancelled or missed session is a deliberate act, kept separate.
 * A function, not a direct update: nobody writes appointments directly
 * (migration 0037), and it refuses sessions that did happen or are already paid.
 */
export const setBillable = (appointmentId, billable) =>
  supabase.rpc('set_appointment_billable', {
    p_appointment_id: appointmentId,
    p_billable: billable,
  });

/** A practice day, given as a day key or any instant within it. */
const dayBounds = (date) => dayRange(typeof date === 'string' ? date : dayKey(date));

export const fetchDayAppointments = async ({ date, therapistId }) => {
  const { start, end } = dayBounds(date);
  let query = supabase
    .from('appointments')
    .select(`
      id, starts_at, ends_at, status, is_billable, billing_mode,
      patient_amount, patient_payment_status, cancellation_reason, location_id,
      dependents ( id, first_name, last_name, date_of_birth ),
      treatments ( consecutive_cancellations, insurance_plans ( name, insurance_companies ( name ) ) ),
      therapists ( id, first_name, last_name ),
      locations ( id, name, is_online )
    `)
    .gte('starts_at', start)
    .lt('starts_at', end)
    .order('starts_at');

  if (therapistId) query = query.eq('therapist_id', therapistId);
  return query;
};

/** Durations come from the event log, never from stored counters. */
export const fetchDayDurations = async ({ date, therapistId }) => {
  const { start, end } = dayBounds(date);
  let query = supabase
    .from('appointment_durations')
    .select('appointment_id, checked_in_at, session_started_at, session_ended_at, waiting_minutes, session_minutes, total_minutes')
    .gte('starts_at', start)
    .lt('starts_at', end);

  if (therapistId) query = query.eq('therapist_id', therapistId);
  return query;
};

// --- professional attendance (their own time in the consulting room) --------

export const fetchOpenAttendance = (therapistId) =>
  supabase
    .from('therapist_attendance')
    .select('*, locations(name)')
    .eq('therapist_id', therapistId)
    .is('checked_out_at', null)
    .maybeSingle();

export const checkInProfessional = ({ therapistId, locationId, source = 'manual' }) =>
  supabase.from('therapist_attendance').insert([{
    therapist_id: therapistId,
    location_id: locationId || null,
    source,
  }]);

export const checkOutProfessional = (attendanceId) =>
  supabase
    .from('therapist_attendance')
    .update({ checked_out_at: new Date().toISOString() })
    .eq('id', attendanceId);

/**
 * Splits a day into what the professional needs to see at a glance: what is
 * happening now, who is waiting, what already happened, and what is still to
 * come.
 */
export const groupDay = (appointments) => {
  const current = [];
  const waiting = [];
  const upcoming = [];
  const previous = [];

  for (const appointment of appointments) {
    if (appointment.status === STATUS.IN_SESSION) current.push(appointment);
    else if (appointment.status === STATUS.WAITING) waiting.push(appointment);
    else if ([STATUS.COMPLETED, STATUS.CANCELLED, STATUS.NO_SHOW].includes(appointment.status)) previous.push(appointment);
    else upcoming.push(appointment);
  }

  return { current, waiting, upcoming, previous };
};
